// "How MeeMail is doing" (U0): the owner's verdicts on what MeeMail showed, and the local timings
// that say how much effort MeeMail costs. Shared by the server (routes, `cli stats:week`) and the
// web (the `!` chooser, the Settings page), with no runtime dependencies. Every word here
// describes MeeMail, never the owner: no streaks, no daily score, no "overdue".

export const VERDICTS = ["should_be_card", "shouldnt_be_here", "draft_rewrite"] as const;
export type Verdict = (typeof VERDICTS)[number];

export const VERDICT_LABEL: Record<Verdict, string> = {
  should_be_card: "Should have been a card",
  shouldnt_be_here: "Shouldn't be here",
  draft_rewrite: "Draft needed rewriting",
};

/** Where the verdict was given: it says what MeeMail had decided about the thread. */
export const VERDICT_SURFACES = [
  "card",
  "focus",
  "inbox",
  "thread",
  "routine",
  "set_aside",
] as const;
export type VerdictSurface = (typeof VERDICT_SURFACES)[number];

export const VERDICT_NOTE_MAX = 280;

export interface VerdictRequest {
  threadKey: string;
  verdict: Verdict;
  surface: VerdictSurface;
  cardId?: string;
  groupId?: string;
  note?: string;
}

export interface VerdictRecorded {
  id: string;
  at: string;
}

/** POST /api/measure/session: a visit began `elapsedMs` before this report reached the server. */
export interface SessionStartRequest {
  id: string;
  elapsedMs: number;
  reason: "load" | "return";
}

export interface TimingSample {
  kind: "card_switch";
  ms: number;
}

/** One 7-day window of local measures. Counts are plain numbers; `null` means no data at all. */
export interface WeekNumbers {
  from: string;
  to: string;
  /** Visits that reached a decision, and the median time from opening to that decision. */
  firstDecision: { visits: number; decided: number; medianMs: number | null };
  /** Minutes the visible app saw input, and on how many days (owner's time zone). */
  active: { minutes: number; days: number };
  /** Rounds that began in the window and ended (or are complete now): finished vs left part-way. */
  rounds: { total: number; finished: number };
  verdicts: Record<Verdict, number>;
  /** Cards that came to the owner (new, or updated back into review). */
  cards: number;
  snoozes: { cards: number; twiceOrMore: number };
  /** Replies sent from a MeeMail draft, and how many went out with light or no edits. */
  drafts: { sent: number; lightEdits: number };
  cardSwitch: { samples: number; medianMs: number | null; p95Ms: number | null };
}

export interface WeekStatsReport {
  generatedAt: string;
  timeZone: string;
  thisWeek: WeekNumbers;
  lastWeek: WeekNumbers;
}

export interface MeasureRow {
  id: string;
  label: string;
  thisWeek: string;
  lastWeek: string;
}

/** Below these, a median says more about chance than about MeeMail. */
export const MIN_VISITS = 3;
export const MIN_SWITCH_SAMPLES = 10;
const NOT_YET = "Not enough data yet";

export function durationWords(ms: number): string {
  if (ms < 1000) return `${Math.max(0, Math.round(ms))} ms`;
  const s = Math.round(ms / 1000);
  if (s < 90) return `${s} ${s === 1 ? "second" : "seconds"}`;
  const m = Math.round(s / 60);
  return `${m} ${m === 1 ? "minute" : "minutes"}`;
}

const of = (n: number, total: number) => `${n} of ${total}`;
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function firstDecision(w: WeekNumbers): string {
  const f = w.firstDecision;
  if (f.medianMs === null || f.decided < MIN_VISITS) return NOT_YET;
  return `Median ${durationWords(f.medianMs)}`;
}

function activeTime(w: WeekNumbers): string {
  if (!w.active.minutes) return NOT_YET;
  const perDay = Math.max(1, Math.round(w.active.minutes / Math.max(1, w.active.days)));
  return `About ${plural(perDay, "minute")} a day, on ${plural(w.active.days, "day")}`;
}

function rounds(w: WeekNumbers): string {
  return w.rounds.total ? of(w.rounds.finished, w.rounds.total) : "No rounds yet";
}

function cardsPerDay(w: WeekNumbers): string {
  if (!w.cards) return "No cards yet";
  const days = Math.max(1, Math.round((Date.parse(w.to) - Date.parse(w.from)) / 86_400_000));
  const perDay = w.cards / days;
  return perDay < 1
    ? `Fewer than 1 (${plural(w.cards, "card")} in all)`
    : `About ${Math.round(perDay)}`;
}

function snoozes(w: WeekNumbers): string {
  if (!w.snoozes.cards) return "No snoozes";
  return `${of(w.snoozes.twiceOrMore, w.snoozes.cards)} snoozed cards`;
}

function drafts(w: WeekNumbers): string {
  return w.drafts.sent ? of(w.drafts.lightEdits, w.drafts.sent) : "No drafts sent yet";
}

function cardSwitch(w: WeekNumbers): string {
  const c = w.cardSwitch;
  if (c.medianMs === null || c.p95Ms === null || c.samples < MIN_SWITCH_SAMPLES) return NOT_YET;
  return `Median ${durationWords(c.medianMs)}, 95% within ${durationWords(c.p95Ms)}`;
}

const verdictCount = (n: number) => (n ? String(n) : "None noted");

const ROWS: { id: string; label: string; value: (w: WeekNumbers) => string }[] = [
  {
    id: "first_decision",
    label: "Time from opening MeeMail to the first decision",
    value: firstDecision,
  },
  { id: "active", label: "Time MeeMail took", value: activeTime },
  { id: "rounds", label: "Rounds finished", value: rounds },
  {
    id: "missed",
    label: "Mail that should have been a card (your verdicts)",
    value: (w) => verdictCount(w.verdicts.should_be_card),
  },
  {
    id: "noise",
    label: "Shown but shouldn't have been (your verdicts)",
    value: (w) => verdictCount(w.verdicts.shouldnt_be_here),
  },
  {
    id: "rewrites",
    label: "Drafts that needed rewriting (your verdicts)",
    value: (w) => verdictCount(w.verdicts.draft_rewrite),
  },
  { id: "cards", label: "Cards per day", value: cardsPerDay },
  { id: "snoozes", label: "Cards snoozed twice or more", value: snoozes },
  { id: "drafts", label: "Drafts sent with light or no edits", value: drafts },
  { id: "card_switch", label: "Time to show the next card", value: cardSwitch },
];

/** The report in plain words, one row per measure, this week beside the week before. */
export function measureRows(report: WeekStatsReport): MeasureRow[] {
  return ROWS.map((r) => ({
    id: r.id,
    label: r.label,
    thisWeek: r.value(report.thisWeek),
    lastWeek: r.value(report.lastWeek),
  }));
}
